"use client";

import React from "react";
import { Briefcase, Calendar, MapPin, Code, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";

interface Project {
    name: string;
    description?: string;
    link?: string;
}

interface Experience {
    title: string;
    company: string;
    companyUrl?: string;
    isCompanyClosed?: boolean;
    location: string;
    startDate: string;
    endDate: string;
    description: string;
    projects: Project[];
    current?: boolean;
}

const experiences: Experience[] = [
    {
        title: "Full Stack Developer",
        company: "Acuitmesh Co.,Ltd",
        companyUrl: "https://acuitmesh.com",
        location: "Bangkok, Thailand",
        startDate: "2024-05-01",
        endDate: "Present",
        description: " Developed and deployed robust applications by translating designs into responsive frontend interfaces using Next.js, building scalable backend microservices with Go and Python, and leveraging containerization and cloud-based solutions for efficient deployment and performance optimization.",
        projects: [],
        current: true
    },
    {
        title: "Front-end Developer Internship",
        company: "Acuitmesh Co.,Ltd",
        companyUrl: "https://acuitmesh.com",
        location: "Bangkok, Thailand",
        startDate: "2024-01-01",
        endDate: "2024-04-31",
        description: "Converted designs into responsive, functional interfaces and integrated APIs to deliver seamless and visually appealing user experiences.",
        projects: []
    },
    {
        title: "Business Analyst Internship",
        company: "Information Technology Unit - Mahidol University Faculty of Medicine Ramathibodi Hospital",
        companyUrl: "https://www.rama.mahidol.ac.th/its/",
        location: "Bangkok, Thailand",
        startDate: "2023-06-01",
        endDate: "2023-08-31",
        description: "Gathering requirements from users and designing clear workflows for developers.Working with SAP, optimized queries by applying selective filters to retrieve only the necessary records and ensuring proper indexing on frequently queried fields.",
        projects: []
    },
    {
        title: "Cross-platform Developer",
        company: "YOU Lab & Solution Co.,Ltd",
        isCompanyClosed: true,
        location: "Remote, Singapore",
        startDate: "2022-01-01",
        endDate: "2023-01-31",
        description: "Develop and maintain cross-platform applications using React Native for mobile and Electron for desktop. Implement UI/UX designs into functional code across multiple platforms. Integrate APIs and backend services to ensure seamless functionality. Optimize performance and ensure smooth user experience on both mobile and desktop. Debug and resolve platform-specific issues efficiently. Package and deploy applications for mobile store and desktop.",
        projects: []
    },
    {
        title: "Full Stack Developer",
        company: "JLR Lab Team",
        companyUrl: "https://jlrlab.me",
        location: "Freelance Team",
        startDate: "2020-01-01",
        endDate: "Present",
        description: "Research and develop innovative solutions for various projects, including web applications, mobile apps, and backend systems. Collaborate with team members to design and implement features using modern technologies. Ensure code quality through testing and code reviews. Stay updated with industry trends and best practices to continuously improve development processes.",
        projects: []
    },
    {
        title: "Full Stack Developer",
        company: "Freelance",
        companyUrl: "https://www.iamickdev.com",
        location: "Remote",
        startDate: "2019-01-01",
        endDate: "Present",  
        description: " Collaborated with clients to gather requirements and deliver custom full-stack solutions using frameworks such as Next.js, Remix, Svelte, Astro, Express, Fastify, Nest, Elysia, and Koa. Designed and developed tailored applications focused on functionality and usability, providing high-quality, end-to-end implementation for diverse freelance projects.",
        projects: []
    }
];

export default function WorkExperienceClient() {
    const calculatePeriod = (startDate: string, endDate: string): { period: string; duration: string } => {
        const start = new Date(startDate);
        const end = endDate === "Present" ? new Date() : new Date(endDate);

        let years = end.getFullYear() - start.getFullYear();
        let months = end.getMonth() - start.getMonth();

        if (months < 0) {
            years--;
            months += 12;
        }

        const startFormatted = start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const endFormatted = endDate === "Present" ? "Present" : end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

        let duration = "";
        if (years > 0) duration += `${years} year${years > 1 ? 's' : ''} `;
        if (months > 0) duration += `${months} month${months > 1 ? 's' : ''}`;
        if (!duration) duration = "Less than 1 month";

        return {
            period: `${startFormatted} - ${endFormatted}`,
            duration: duration.trim()
        };
    };

    return (
        <div className="mt-8">
            <div className="relative">
                {/* Animated Timeline line */}
                <div
                    className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 h-full w-0.5 bg-terminal-blue/30"
                    style={{ transformOrigin: "top" }}
                />

                {experiences.map((exp, index) => (
                    <motion.div
                        key={index}
                        className={`relative flex items-center w-full mb-8 ${index % 2 === 0 ? 'md:justify-start' : 'md:justify-end'}`}
                        initial={{
                            opacity: 0,
                            x: index % 2 === 0 ? -100 : 100
                        }}
                        whileInView={{
                            opacity: 1,
                            x: 0
                        }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{
                            duration: 0.6,
                            delay: index * 0.2,
                            ease: "easeOut"
                        }}
                    >
                        {/* Animated Timeline dot with year */}
                        <div className="absolute left-0 md:left-1/2 transform -translate-x-1/2 z-10">
                            <motion.div
                                className="relative flex items-center justify-center"
                                initial={{ scale: 0 }}
                                whileInView={{ scale: 1 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: index * 0.2 + 0.3,
                                    type: "spring",
                                    stiffness: 200
                                }}
                            >
                                <motion.div
                                    className="w-4 h-4 bg-terminal-cyan rounded-full border-2 border-background"
                                    whileHover={{ scale: 1.2 }}
                                    transition={{ type: "spring", stiffness: 400 }}
                                />

                                {/* Year label positioned relative to dot */}
                                <motion.div
                                    className={`hidden md:block absolute whitespace-nowrap text-xs font-semibold text-terminal-blue ${index % 2 === 0
                                        ? 'left-6' // Card on left, year on right
                                        : 'right-6'   // Card on right, year on left
                                        }`}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.3,
                                        delay: index * 0.2 + 0.5
                                    }}
                                >
                                    {calculatePeriod(exp.startDate, exp.endDate).period} ({calculatePeriod(exp.startDate, exp.endDate).duration})
                                </motion.div>
                            </motion.div>
                        </div>

                        {/* Animated Content card */}
                        <motion.div
                            className={`terminal-glass p-4 rounded-lg ml-8 md:ml-0 ${index % 2 === 0 ? 'md:mr-auto md:ml-8' : 'md:ml-auto md:mr-8'} w-full md:w-5/12`}
                            whileHover={{
                                scale: 1.02,
                                boxShadow: "0 10px 30px rgba(0, 255, 255, 0.1)"
                            }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            {/* Header */}
                            <motion.div
                                className="flex items-start justify-between mb-2"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: index * 0.2 + 0.6
                                }}
                            >
                                <div>
                                    <h4 className="text-terminal-green font-semibold">{exp.title}</h4>
                                    <div className="text-terminal-pink text-sm flex items-center gap-1 group">
                                        {exp.companyUrl ? (
                                            <Link 
                                                href={exp.companyUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="hover:underline flex items-center gap-1"
                                            >
                                                {exp.company}
                                                <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </Link>
                                        ) : (
                                            <span>{exp.company}</span>
                                        )}
                                        {exp.isCompanyClosed && (
                                            <Badge variant="destructive" className="text-xs ml-1">
                                                Defunct
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                {exp.current && (
                                    <motion.span
                                        className="text-xs bg-terminal-cyan/20 text-terminal-cyan px-2 py-1 rounded"
                                        initial={{ scale: 0 }}
                                        whileInView={{ scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{
                                            type: "spring",
                                            delay: index * 0.2 + 0.8
                                        }}
                                    >
                                        Current
                                    </motion.span>
                                )}
                            </motion.div>

                            {/* Meta info */}
                            <motion.div
                                className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3"
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: index * 0.2 + 0.7
                                }}
                            >
                                <span className="flex md:hidden items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {calculatePeriod(exp.startDate, exp.endDate).period} ({calculatePeriod(exp.startDate, exp.endDate).duration})
                                </span>
                                <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {exp.location}
                                </span>
                            </motion.div>

                            {/* Description */}
                            <motion.p
                                className="text-sm mb-3"
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: index * 0.2 + 0.8
                                }}
                            >
                                {exp.description}
                            </motion.p>

                            {/* Projects */}
                            { exp.projects && exp.projects.length > 0 &&
                            <motion.div
                                className="space-y-1"
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: index * 0.2 + 0.9
                                }}
                            >
                                <p className="text-xs font-medium text-terminal-yellow flex items-center gap-1">
                                    <Code className="h-3 w-3" />
                                    Key Projects:
                                </p>
                                <ul className="text-xs space-y-1">
                                    {exp.projects.map((project, pIndex) => (
                                        <motion.li
                                            key={pIndex}
                                            className="flex items-start gap-1"
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{
                                                duration: 0.3,
                                                delay: index * 0.2 + 1 + (pIndex * 0.1)
                                            }}
                                        >
                                            <span className="text-terminal-blue mt-0.5">•</span>
                                            <div className="flex-1">
                                                {project.link ? (
                                                    <Link
                                                        href={project.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hover:text-terminal-cyan transition-colors flex items-center gap-1 group"
                                                    >
                                                        <span>{project.name}</span>
                                                        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </Link>
                                                ) : (
                                                    <span className="text-terminal-cyan">{project.name}</span>
                                                )}
                                                {project.description && (
                                                    <p className="text-terminal-gray text-xs mt-0.5 opacity-80">
                                                        {project.description}
                                                    </p>
                                                )}
                                            </div>
                                        </motion.li>
                                    ))}
                                </ul>
                            </motion.div>
                            }
                        </motion.div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}